import _, {differenceBy} from 'lodash';
import memoize from 'lru-memoize';
import React, {FC, useEffect, useState} from 'react';

import {
  useBuildCancelToken,
  useDebounce,
} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ExtendedError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {CancelError} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {SimpleDropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown';
import {useFetchApi} from '../../../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../../../srcCommon/services/bugsnag/bugsnag';
import {
  doesInboxMatchQuery,
  doesTeammateMatchQuery,
  teammateCanAccessInbox,
} from '../../../../helpers/modelHelpers';
import {buildInboxSearchUrl} from '../../../../helpers/models/inboxHelpers';
import {buildTeammateSearchUrl, SearchTeammateSortsEnum} from '../../../../helpers/models/teammateHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {ApiSearchInboxes} from '../../../../models/api/searchInboxModel';
import {ApiSearchTeammates} from '../../../../models/api/searchTeammateModel';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../../../../models/api/teammateModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {fetchApiAsync} from '../../../../services/http/http';
import {ResourceMap} from '../../../../store/appTypes';
import {FindOnlineStatus} from '../../../../store/data/teammates/onlineTeammatesSelectors';
import {MentionDropdownItem} from '../../../common/dropdowns/autocomplete/mentionDropdownItem';
import {DropdownSeparator} from '../../../common/dropdowns/dropdownSeparator';
import {MoveDropdownItem} from '../conversation/topbar/move/moveDropdownItem';

/*
 * Props.
 */

interface InternalRecipientsDropdownProps extends DropdownProps {
  teamsByNamespace: ResourceMap<TeamModel>;
  findOnlineStatus: FindOnlineStatus;
  onTeammateSelect: HandlerOf<TeammateModel>;
  onInboxSelect: HandlerOf<InboxModel>;
  query: string;
  isInboxSelected: boolean;
  availableInboxes: ReadonlyArray<InboxModel>;
  user: TeammateModel;
  companyUrl: string;
  visibleInboxIds: ReadonlySet<number>;
  mentionableTeammates: ReadonlyArray<TeammateModel>;
  teammates: ReadonlyArray<TeammateModel>;
  canMoveToInaccessibleInbox: boolean;
  hasLazyTeammatesDropdowns: boolean;
}

/*
 * Component.
 */

export const InternalRecipientsDropdown: FC<InternalRecipientsDropdownProps> = (props) => {
  const {query} = props;

  const teammates = useTeammates(props);
  const inboxes = useInboxes(props);

  if (query.length === 0) {
    return null;
  }

  if (teammates.length === 0 && inboxes.length === 0) {
    return null;
  }

  return (
    <SimpleDropdown {...props} shouldSelectFirst shouldSelectWithTab>
      {teammates.map((teammate) => (
        <MentionDropdownItem
          onSelect={() => props.onTeammateSelect(teammate)}
          key={teammate.id}
          teammate={teammate}
          onlineStatus={props.findOnlineStatus(teammate)}
          isTeamMember
        />
      ))}
      {teammates.length > 0 && inboxes.length > 0 && <DropdownSeparator />}
      {inboxes.map((inbox) => (
        <MoveDropdownItem
          key={inbox.id}
          inbox={inbox}
          onSelect={() => props.onInboxSelect(inbox)}
          team={props.teamsByNamespace[inbox.namespace]}
          shouldShowDeduplication={inboxes.filter((i) => i.name === inbox.name).length > 1}
          shouldShowTeammateCount
          isBold
        />
      ))}
    </SimpleDropdown>
  );
};

/*
 * Helpers.
 */

const maxTeammatesToDisplay = 5;
const maxInboxesToDisplay = 3;

function useTeammates(props: InternalRecipientsDropdownProps) {
  const {query, hasLazyTeammatesDropdowns} = props;

  // Only show teammates if the query is not empty.
  const shouldShowTeammates = query.length > 0;

  // Only fetch the teammates async if we have lazy teammates.
  // (Otherwise, all teammates should be in the store already.)
  const shouldFetchAsync = hasLazyTeammatesDropdowns;

  const syncTeammates = useSyncTeammates(props, shouldShowTeammates && !shouldFetchAsync);
  const asyncTeammates = useAsyncTeammates(props, shouldShowTeammates && shouldFetchAsync);

  if (!shouldShowTeammates) {
    return [];
  }

  if (shouldFetchAsync) {
    return asyncTeammates;
  }

  return syncTeammates;
}

function useAsyncTeammates(props: InternalRecipientsDropdownProps, shouldUseAsyncTeammates: boolean) {
  const {buildCancelToken} = useBuildCancelToken();
  const [loadedTeammates, setLoadedTeammates] = useState<ReadonlyArray<TeammateModel>>([]);

  const {query, companyUrl, user, teammates} = props;

  const fetchTeammatesAsync = async (queryToFetch: string) => {
    // Build a new cancel token, canceling the previous one.
    const cancelToken = buildCancelToken();

    // Build the URL and options.
    const url = buildTeammateSearchUrl(companyUrl, queryToFetch, {
      limit: maxTeammatesToDisplay,
      accountStatuses: [TeammateAccountStatusesEnum.ACTIVE],
      licenseTypes: [TeammateLicenseTypesEnum.FULL],
      excludedTeammateIds: [user.id],
      sort: SearchTeammateSortsEnum.NAME,
    });
    try {
      const searchTeammates = await fetchApiAsync(ApiSearchTeammates, url, {cancelToken});
      setLoadedTeammates(differenceBy(searchTeammates.teammates, teammates, (t) => t.id));
    } catch (error) {
      if (error instanceof CancelError) {
        return;
      }

      reportToBugsnag(new ExtendedError('Error fetching internal recipient teammates async', error));
    }
  };

  const fetchTeammatesAsyncDebounced = useDebounce(fetchTeammatesAsync, 100);

  useEffect(() => {
    if (!shouldUseAsyncTeammates) {
      return;
    }

    // Fetch the teammates.
    fetchTeammatesAsyncDebounced(query);
  }, [query, shouldUseAsyncTeammates, fetchTeammatesAsyncDebounced]);

  if (!shouldUseAsyncTeammates) {
    return [];
  }

  return loadedTeammates;
}

function useSyncTeammates(props: InternalRecipientsDropdownProps, shouldUseSyncTeammates: boolean) {
  if (!shouldUseSyncTeammates) {
    return [];
  }

  const {query, mentionableTeammates, teammates} = props;
  return filterTeammates(mentionableTeammates, teammates, query);
}

const filterTeammates = memoize(20)(
  (
    mentionableTeammates: ReadonlyArray<TeammateModel>,
    teammates: ReadonlyArray<TeammateModel>,
    query: string,
  ) => {
    // Shorcut for empty query.
    if (!query) {
      return mentionableTeammates;
    }

    const newQuery = query.replace(/^@/, '').toLowerCase();

    // Otherwise, filter by name.
    return _(mentionableTeammates)
      .difference(teammates)
      .filter((teammate) => doesTeammateMatchQuery(teammate, newQuery))
      .take(maxTeammatesToDisplay)
      .value();
  },
);

function useInboxes(props: InternalRecipientsDropdownProps) {
  const {query, isInboxSelected, canMoveToInaccessibleInbox} = props;

  // Only show inboxes if there's no inbox already selected and the query is not empty.
  const shouldShowInboxes = !isInboxSelected && query.length > 0;

  // Only fetch the inboxes async if we can move to inaccessible ones.
  // (Otherwise, all inboxes should be in the store already.)
  const shouldFetchAsync = canMoveToInaccessibleInbox;

  const syncInboxes = useSyncInboxes(props, shouldShowInboxes && !shouldFetchAsync);
  const asyncInboxes = useAsyncInboxes(props, shouldShowInboxes && shouldFetchAsync);

  if (!shouldShowInboxes) {
    return [];
  }

  if (shouldFetchAsync) {
    return asyncInboxes;
  }

  return syncInboxes;
}

function useAsyncInboxes(props: InternalRecipientsDropdownProps, shouldUseAsyncInboxes: boolean) {
  const {query, companyUrl} = props;

  const [loadedInboxes, setLoadedInboxes] = useState<ReadonlyArray<InboxModel>>([]);
  const fetchInboxesApi = useFetchApi({
    apiType: ApiSearchInboxes,
    debounceDelay: 100,
    onStateChange: (state) => {
      if (state.asyncStatus === AsyncStatusesEnum.LOADED) {
        setLoadedInboxes(state.result?.inboxes || []);
      }
    },
  });

  useEffect(() => {
    if (!shouldUseAsyncInboxes) {
      return;
    }

    // Build the URL and options.
    const url = buildInboxSearchUrl(companyUrl, query, {
      limit: maxInboxesToDisplay,
    });
    fetchInboxesApi(url);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, shouldUseAsyncInboxes]);

  if (!shouldUseAsyncInboxes) {
    return [];
  }

  return loadedInboxes;
}

function useSyncInboxes(props: InternalRecipientsDropdownProps, shouldUseSyncInboxes: boolean) {
  if (!shouldUseSyncInboxes) {
    return [];
  }

  const {query, availableInboxes, user, visibleInboxIds} = props;
  return filterInboxes(availableInboxes, visibleInboxIds, user, query);
}

const filterInboxes = memoize(20)(
  (
    availableInboxes: ReadonlyArray<InboxModel>,
    visibleInboxIds: ReadonlySet<number>,
    user: TeammateModel,
    query: string,
  ) => {
    // Shorcut for empty query.
    if (!query) {
      return availableInboxes;
    }

    const newQuery = query.replace(/^@/, '').toLowerCase();

    // Otherwise, filter by name, sorting pinned inboxes and inboxes the user is a member of to the top.
    return _(availableInboxes)
      .filter((i) => doesInboxMatchQuery(i, newQuery))
      .sortBy([(i) => !teammateCanAccessInbox(user, i), (i) => !visibleInboxIds.has(i.id)])
      .take(maxInboxesToDisplay)
      .value();
  },
);
